import { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/landing-v2/Header";
import Footer from "@/components/landing-v2/Footer";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const ARTICLE_TITLE = "Hooki w social media: kompletny poradnik";
const ARTICLE_DESCRIPTION = "Czym jest hook, dlaczego decyduje o wszystkim i jak pisać hooki, które zatrzymują scrollowanie? 10 formatów, kąty, emocje i złota formuła.";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" as const },
  }),
};

const BlogHooki = () => {
  useEffect(() => {
    document.title = ARTICLE_TITLE;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", ARTICLE_DESCRIPTION);
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "description";
      newMeta.content = ARTICLE_DESCRIPTION;
      document.head.appendChild(newMeta);
    }
    return () => {
      document.title = "Viraluj – Przestań wymyślać. Zacznij robić viralowe wideo";
    };
  }, []);

  return (
    <div className="min-h-screen text-gray-900">
      <Header />

      <section className="relative pt-28 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9B896] via-[#FABFA3] to-[#FCCAB0]" />

        <article className="relative z-10 max-w-3xl mx-auto px-4">
          {/* Back link */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Wróć do bloga
            </Link>
          </motion.div>

          {/* Hero */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1} className="mb-10">
            <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 mb-4">
              Strategia
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-tight">
              Hooki w social media: kompletny poradnik
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                9 kwi 2026
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                15 min czytania
              </span>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-6 sm:p-10 prose prose-gray max-w-none"
          >
            <p className="lead text-lg text-gray-700">
              Hook to pierwsze 1–3 sekundy treści — zdanie otwierające opis, pierwsze słowa w wideo, kadr otwierający rolkę. To jedyny element, który decyduje, czy ktoś zostaje, czy scrolluje dalej.
            </p>

            <p>
              Uwaga człowieka na urządzeniach mobilnych wynosi przeciętnie mniej niż 2 sekundy na podjęcie decyzji „zostać czy scrollować". Na TikToku algorytm mierzy retencję już po 3 sekundach — jeśli ludzie odpadają w tym miejscu, zasięg spada. Na Instagramie opis skraca się po pierwszej linii i użytkownik musi kliknąć „więcej" — ta pierwsza linia to hook. Na YouTube thumbnail i tytuł pełnią funkcję pre-hooka, zanim film w ogóle się uruchomi.
            </p>

            <p>
              Dobry hook nie jest clickbaitem. Clickbait obiecuje i nie dostarcza — niszczy zaufanie, a algorytmy karzą treści z wysokim bounce rate i niskim completion rate. Dobry hook to obietnica, którą treść spełnia.
            </p>

            <p>
              Według badań, treści z silnymi hookami generują 3–5× wyższe wskaźniki zaangażowania w porównaniu z treściami bez wyraźnego otwarcia. Na TikToku hooki oparte na luce informacyjnej zwiększają completion rate o 234% względem prostych stwierdzeń.
            </p>

            <h2>Anatomia hooka: trzy warstwy</h2>

            <p>Każdy skuteczny hook składa się z trzech elementów, które mogą działać razem lub samodzielnie:</p>

            <ul>
              <li><strong>Warstwa 1 — Format</strong> (jak hook jest zbudowany strukturalnie): pytanie, twierdzenie, liczba, historia, kontrast.</li>
              <li><strong>Warstwa 2 — Kąt</strong> (perspektywa, z której podchodzisz do tematu): problem, kontrariański, ekskluzywność, transformacja, błąd.</li>
              <li><strong>Warstwa 3 — Emocja</strong> (co hook wywołuje u odbiorcy): ciekawość, strach, ekscytacja, identyfikacja, FOMO, nostalgia.</li>
            </ul>

            <p>
              Najsilniejsze hooki łączą wszystkie trzy warstwy. Przykład: „Większość ludzi popełnia ten jeden błąd i przez to traci połowę efektów" — format (twierdzenie z konkretem), kąt (błąd), emocja (lęk przed porażką + identyfikacja).
            </p>

            <h2>10 formatów hooków</h2>

            <h3>1. Hook pytający (Question Hook)</h3>

            <p>
              Pytanie zmusza mózg do szukania odpowiedzi — to odruch bezwarunkowy. Najskuteczniejsze pytania nie są retoryczne, lecz kwestionują założenia lub dotykają realnego bólu odbiorcy.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„Czy wiesz, dlaczego [powszechna czynność] nie działa tak, jak myślisz?"</li>
              <li>„Co byś zrobił, gdybyś jutro musiał [stanąć twarzą w twarz z problemem]?"</li>
              <li>„Ile razy próbowałeś [działanie] i to nie wychodziło?"</li>
              <li>„Dlaczego [efekt], mimo że [logiczne założenie]?"</li>
            </ul>

            <p>
              <strong>Pułapka:</strong> Pytania zbyt szerokie lub zbyt ogólne nie działają. „Chcesz więcej energii?" to słaby hook — każdy chce. „Dlaczego kawa przestaje działać po 3 tygodniach codziennego picia?" — to już działa, bo jest precyzyjne i kontraintuicyjne.
            </p>

            <h3>2. Hook kontrariański (Contrarian Hook)</h3>

            <p>
              Wypowiedź, która przeczy powszechnemu przekonaniu, wywołuje dysonans poznawczy. Mózg automatycznie zatrzymuje się, gdy napotka coś sprzecznego z tym, co wie lub w co wierzy.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„Przestań [powszechna praktyka]. To niszczy twój [efekt]."</li>
              <li>„Wszystko, co wiesz o [temacie], jest błędne."</li>
              <li>„[Popularne przekonanie] to mit. Oto prawda."</li>
              <li>„Im więcej [X], tym gorszy [efekt] — i przez lata robiłeś odwrotnie."</li>
            </ul>

            <p>
              <strong>Pułapka:</strong> Hook musi być prawdziwy i możliwy do obrony. Kontrariańskie twierdzenie bez dowodu w treści to clickbait i utrata zaufania.
            </p>

            <h3>3. Hook liczbowy (Statistics Hook)</h3>

            <p>
              Konkretna liczba jest bardziej wiarygodna niż ogólne twierdzenie. Liczby przyciągają wzrok w feedzie, sygnalizują precyzję i sugerują wiedzę ekspercką.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„[X]% ludzi popełnia ten błąd przy [czynności]."</li>
              <li>„W ciągu [X] dni [konkretna zmiana]. Oto jak."</li>
              <li>„[X] rzeczy, których nie powinieneś robić przed [sytuacją]."</li>
              <li>„Przetestowałem [liczba] metod. Tylko [liczba] naprawdę działa."</li>
            </ul>

            <p>
              <strong>Pułapka:</strong> Liczby muszą być prawdziwe i weryfikowalne. Listy „7 rzeczy" zamiast „kilka rzeczy" działają lepiej, bo precyzja sugeruje ekspertyzę.
            </p>

            <h3>4. Hook narracyjny (Story Hook)</h3>

            <p>
              Opowieść jest najstarszym mechanizmem przykuwania uwagi. Mózg automatycznie angażuje się w historię — chcemy wiedzieć, jak się kończy.
            </p>

            <p><strong>In medias res</strong> — zacznij od środka akcji:</p>
            <ul>
              <li>„Stałem przed 200-osobową widownią i nagle zapomniałem, o czym miałem mówić."</li>
              <li>„Dwa lata temu straciłem wszystkich klientów w jeden tydzień."</li>
            </ul>

            <p><strong>Setup + napięcie</strong> — buduj napięcie przez konkretny szczegół:</p>
            <ul>
              <li>„Próbowałem [działanie] przez [czas]. Oto czego się nauczyłem."</li>
              <li>„Gdybym wiedział to 5 lat temu, uniknąłbym [konkretny błąd]."</li>
              <li>„Przez [czas] wierzyłem, że [błędne przekonanie]. Myliłem się."</li>
            </ul>

            <p>
              <strong>Pułapka:</strong> Historia musi być prawdziwa lub oparta na realnym doświadczeniu. Zacznij zawsze od momentu najwyższego napięcia, nie od tła.
            </p>

            <h3>5. Hook luki informacyjnej (Curiosity Gap Hook)</h3>

            <p>
              Kiedy wiemy, że czegoś nie wiemy — i że ta wiedza jest dla nas istotna — odczuwamy dyskomfort, który pchnie nas do wypełnienia luki. Hook celowo tworzy tę lukę i wstrzymuje odpowiedź.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„Jest jeden powód, dla którego [powszechny wysiłek] nie daje efektów — i nikt o tym nie mówi."</li>
              <li>„Nie wiesz jeszcze o [X], a powinieneś."</li>
              <li>„Robiłem to źle przez [czas]. Dopiero niedawno odkryłem, co naprawdę zmienia wyniki."</li>
              <li>„To, co zaraz pokażę, zmieni sposób, w jaki patrzysz na [temat]."</li>
            </ul>

            <p>
              <strong>Pułapka:</strong> Treść musi być równie dobra co obietnica w hooku. Jeśli luka jest sztuczna, a odpowiedź banalna, tracisz obserwatora.
            </p>

            <h3>6. Hook bólowy (Pain Point Hook)</h3>

            <p>
              Bezpośrednie nazwanie problemu odbiorcy wywołuje natychmiastowe rozpoznanie. „To o mnie" — i człowiek zostaje, bo czuje, że ta treść jest dla niego.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„Czujesz się [stan], mimo że robisz [wysiłek]?"</li>
              <li>„Spędzasz [czas] na [działanie] i tak nie osiągasz [efektu]?"</li>
              <li>„Próbujesz [działanie] od [czas] i nadal tkwisz w miejscu?"</li>
            </ul>

            <p>
              <strong>Pułapka:</strong> Bądź precyzyjny — „masz problemy z produktywnością?" to za ogólne. „Kończysz dzień z listą zrób to samo długą co rano?" to już konkret trafiający w punkt.
            </p>

            <h3>7. Hook społeczny (Social Proof Hook)</h3>

            <p>
              Ludzie naśladują innych — to biologiczny mechanizm redukcji ryzyka. Hook oparty na social proof skraca dystans do zaufania.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„[X] osób robi to codziennie. Oto dlaczego."</li>
              <li>„To najczęstsze pytanie, jakie dostaję na temat [tematu]."</li>
              <li>„Eksperci [branży] wiedzą o tym od lat. Większość ludzi — nie."</li>
              <li>„Setki komentarzy z tym samym pytaniem. Czas odpowiedzieć."</li>
            </ul>

            <h3>8. Hook transformacyjny (Before-After Hook)</h3>

            <p>
              Pokazuje drogę od punktu A (problem) do punktu B (rozwiązanie). Najprostszy i jeden z najsilniejszych schematów narracyjnych.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„Jak przeszedłem od [problem] do [rezultat] w [czas]."</li>
              <li>„[X] tygodni temu wyglądało to tak. Dziś wygląda tak."</li>
              <li>„Jedna zmiana. Dwa tygodnie. [Konkretny wynik]."</li>
            </ul>

            <h3>9. Hook imperatywny (Command Hook)</h3>

            <p>
              Bezpośrednie polecenie działa, gdy odbiorca intuicyjnie rozumie, że to w jego interesie. Tryb rozkazujący skraca dystans i działa szybciej niż pytanie.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„Przestań robić [czynność]. Natychmiast."</li>
              <li>„Zanim [typowa czynność], przeczytaj/obejrzyj to."</li>
              <li>„Zapisz to. Będziesz potrzebował za rok."</li>
              <li>„Nie kupuj [produktu], dopóki nie zobaczysz tego."</li>
            </ul>

            <h3>10. Hook ekskluzywny (Insider Hook)</h3>

            <p>
              Ludzie chcą być „w środku" — mieć dostęp do wiedzy, której inni nie mają. Hook ekskluzywny aktywuje FOMO bez sztucznej presji sprzedażowej.
            </p>

            <p>Wzorce:</p>
            <ul>
              <li>„Tego nie piszą na opakowaniu / w instrukcji / w kursie, ale [fakt]."</li>
              <li>„Po [liczba] latach [działanie] odkryłem, że [zaskakujący wniosek]."</li>
              <li>„Specjaliści wiedzą o tym od zawsze. Reszta — nie."</li>
              <li>„Nikt nie mówi o tej metodzie, bo [powód]."</li>
            </ul>

            <h2>Kąty hooka — perspektywa, z której podchodzisz do tematu</h2>

            <p>
              Ten sam temat można pokazać z wielu różnych kątów — każdy trafi do innej części odbiorców i wywoła inną reakcję. Zasada: jeden post = jeden kąt. Mieszanie kątów rozmywa przekaz.
            </p>

            <p>10 kątów z przykładami dla tematu „produktywność":</p>

            <ul>
              <li><strong>Błąd odbiorcy:</strong> „90% ludzi zarządza czasem w zły sposób — i przez to robi więcej, osiągając mniej."</li>
              <li><strong>Obalenie mitu:</strong> „Wstawanie o 5 rano nie czyni cię produktywnym. Mit rozbity."</li>
              <li><strong>Problem:</strong> „Twoja lista zadań rośnie mimo że pracujesz więcej? To nie twoja wina."</li>
              <li><strong>Transformacja:</strong> „Jak zredukowałem godziny pracy o połowę i zaczęłem robić więcej."</li>
              <li><strong>Ekskluzywność:</strong> „Najlepsi operatorzy na świecie używają tej techniki. Większość coachów jej nie uczy."</li>
              <li><strong>Porównanie:</strong> „Deep work vs time blocking — co naprawdę działa dla twórców?"</li>
              <li><strong>Historia osobista:</strong> „Przez 2 lata byłem zajęty 16h dziennie i nie robiłem nic ważnego. Oto co zmieniłem."</li>
              <li><strong>Dane:</strong> „Przetestowałem 12 systemów produktywności. Tylko jeden działał dłużej niż miesiąc."</li>
              <li><strong>Kontrariański:</strong> „Przestań optymalizować poranek. To niepopularna opinia."</li>
              <li><strong>Social proof:</strong> „Tysiące twórców zadaje mi to samo pytanie. Oto odpowiedź."</li>
            </ul>

            <h2>Emocje w hookach</h2>

            <p>
              Emocja to paliwo, które sprawia, że hook zatrzymuje scrollowanie i pozostaje w pamięci. Każdy format i każdy kąt można wzmocnić konkretną emocją.
            </p>

            <h3>Ciekawość</h3>
            <p>
              Najbardziej uniwersalna emocja w social media. Jak wywołać: luka informacyjna, pytanie bez odpowiedzi w hooku, zapowiedź zaskakującego wniosku.
            </p>
            <p><em>Przykład: „Jest jeden powód, dla którego twoja strategia contentowa nie działa — i to nie to, co myślisz."</em></p>

            <h3>Strach przed błędem</h3>
            <p>
              Biologicznie silniejszy niż obietnica zysku. Mózg jest bardziej zmotywowany do unikania straty niż do zdobycia zysku. Jak wywołać: nazwij błąd, pokaż konsekwencję, użyj imperatywu „przestań", „uwaga", „zanim".
            </p>
            <p><em>Przykład: „Nadal stosujesz tę technikę? Niszczy twoje wyniki bez wiedzy."</em></p>

            <h3>FOMO (Fear of Missing Out)</h3>
            <p>
              Skuteczne przy premierach, trendach, limitowanych informacjach. Jak wywołać: ekskluzywność, ograniczony czas, sygnał że „wszyscy już wiedzą".
            </p>
            <p><em>Przykład: „Ta zmiana algorytmu zaczęła działać 3 dni temu. Kto nie wie — traci zasięgi."</em></p>

            <h3>ROMO (Relief of Missing Out)</h3>
            <p>
              Przeciwieństwo FOMO. Działa, gdy odbiorca jest zmęczony trendami i presją optymalizacji. Przynosi ulgę: „nie musisz tego robić".
            </p>
            <p><em>Przykład: „Nie musisz być na każdej platformie. Jedna platforma, zrobiona dobrze, wystarczy."</em></p>

            <h3>Identyfikacja</h3>
            <p>
              „To właśnie ja" — jeden z najszybszych mechanizmów zatrzymania scrollowania. Odbiorca widzi siebie w opisywanej sytuacji i nie może nie kliknąć.
            </p>
            <p><em>Przykład: „Kończysz dzień z poczuciem, że byłeś zajęty, ale nic ważnego się nie zrobiło?"</em></p>

            <h3>Inspiracja</h3>
            <p>
              Motywacja, poczucie możliwości, obietnica zmiany. Jak wywołać: historia sukcesu z konkretnymi wynikami, obietnica transformacji.
            </p>
            <p><em>Przykład: „12 tygodni temu zaczął od zera. Dziś ma [konkretny wynik]. Oto co zrobił."</em></p>

            <h3>Złość / frustracja</h3>
            <p>
              Kontrowersyjne, ale skuteczne. Dobrze targetowana frustracja buduje społeczność przez wspólnego „wroga" — zły system, branżową dezinformację, nieuczciwe praktyki.
            </p>
            <p><em>Przykład: „Marki X nie chcą, żebyś wiedział, że [fakt], bo tracą na tym pieniądze."</em></p>

            <h3>Nostalgia</h3>
            <p>
              Powrót do wspomnień, lepszych czasów. Szczególnie skuteczna, gdy łączy się z obecnym problemem.
            </p>
            <p><em>Przykład: „Pamiętasz, kiedy social media były proste? Oto dlaczego wracam do tamtych zasad."</em></p>

            <h2>Style narracyjne hooków</h2>

            <p>Styl to sposób, w jaki hook jest napisany — ton, tempo, głos narratora. Ten sam format i kąt brzmi inaczej w różnym stylu.</p>

            <ul>
              <li><strong>Styl bezpośredni</strong> — krótko, konkretnie, na temat. Działa na TikToku i Reels.</li>
              <li><strong>Styl konwersacyjny</strong> — jak rozmowa z przyjacielem. Buduje relację i zaufanie. YouTube, blog, TikTok.</li>
              <li><strong>Styl prowokacyjny</strong> — celowo śmiały, wchodzi w konflikt z powszechnym przekonaniem. LinkedIn, Twitter/X.</li>
              <li><strong>Styl narracyjny</strong> — wciąga w historię, buduje napięcie. Najdłuższy, ale najwyższy retention. YouTube, LinkedIn.</li>
              <li><strong>Styl ekspercki</strong> — podkreśla kompetencję przez sygnały autorytetu. LinkedIn, YouTube, edukacja.</li>
              <li><strong>Styl emocjonalny</strong> — stawia emocję przed informacją. Personal branding, storytelling.</li>
            </ul>

            <h2>Hooki wideo — trzy równoległe warstwy</h2>

            <p>W treściach wideo hook ma trzy warstwy działające jednocześnie:</p>

            <ul>
              <li><strong>Warstwa wizualna</strong> — co widać w pierwszym kadrze? Ruch, kontrast, nieoczekiwany obiekt, wyraz twarzy. 75% użytkowników ogląda wideo bez dźwięku — wizual musi działać samodzielnie.</li>
              <li><strong>Warstwa tekstowa</strong> — napis nakładany na ekran w pierwszych 1–2 sekundach. Kluczowy dla odbiorców oglądających bez dźwięku. Max jedno zdanie.</li>
              <li><strong>Warstwa werbalna</strong> — pierwsze słowa na kamerze lub muzyka przerywająca wzorzec. Ton głosu, tempo mówienia i pierwsze zdanie decydują o retencji.</li>
            </ul>

            <p>
              <strong>Pattern interrupt</strong> — celowe złamanie oczekiwanego schematu. Nagłe cięcie, nieoczekiwany ruch, tekst pojawiający się przed twarzą. Mózg ewolucyjnie wychwytuje zmiany w otoczeniu i ten mechanizm zatrzymuje scrollowanie.
            </p>

            <p>
              Najczęstszy błąd w hookach wideo: „Hej, dzisiaj w filmie..." — to natychmiastowa śmierć zasięgu. Każda sekunda, która nie daje wartości, to sekunda, po której odbiorca scrolluje dalej. Zacznij zawsze od meritum.
            </p>

            <h2>Hooki platformospecyficzne</h2>

            <h3>TikTok</h3>
            <ul>
              <li>Okno uwagi: 2 sekundy</li>
              <li>Algorytm mierzy: completion rate i rewatche</li>
              <li>Najlepsze formaty: luka informacyjna, pattern interrupt wizualny, pytanie</li>
              <li>Ton: szybki, energetyczny, autentyczny — niski poziom produkcji często działa lepiej</li>
              <li>Tekst na ekranie: kluczowy — większość ogląda bez dźwięku</li>
            </ul>

            <h3>Instagram Reels</h3>
            <ul>
              <li>Okno uwagi: 2–3 sekundy</li>
              <li>Algorytm mierzy: zapisania i udostępnienia (nie tylko lajki)</li>
              <li>Najlepsze formaty: transformacja wizualna, hook bólowy, liczby, ekskluzywny</li>
              <li>Opis: pierwsze zdanie = hook tekstowy, musi zachęcać do kliknięcia „więcej"</li>
            </ul>

            <h3>YouTube (długi format)</h3>
            <ul>
              <li>Okno uwagi: 8–15 sekund</li>
              <li>Pre-hook: thumbnail + tytuł decydują zanim film się uruchomi</li>
              <li>Najlepsze formaty: historia, kontrariański, luka informacyjna, transformacja</li>
              <li>Potwierdź w pierwszych 30 sekundach, że film spełni obietnicę z tytułu</li>
            </ul>

            <h3>YouTube Shorts</h3>
            <ul>
              <li>Okno uwagi: 2–3 sekundy</li>
              <li>Działa jak TikTok — szybko, konkretnie, bez wstępu</li>
              <li>Edukacja i how-to dominują w tym formacie</li>
            </ul>

            <h3>LinkedIn</h3>
            <ul>
              <li>Okno uwagi: 3–5 sekund (bardziej intelektualna platforma)</li>
              <li>Algorytm mierzy: komentarze i udostępnienia, nie lajki</li>
              <li>Opis skraca się po 3 linijkach — pierwsze 3 linie = hook</li>
              <li>Najlepsze formaty: kontrariański, ekspercki, historia osobista, dane</li>
              <li>Co działa wyjątkowo: kontrowersyjna opinia poparta argumentem</li>
            </ul>

            <h2>Złota formuła i system oceny</h2>

            <p>Każdy silny hook zawiera trzy elementy:</p>

            <ol>
              <li><strong>Curiosity Gap</strong> — otwórz pętlę, zasygnalizuj że czegoś brakuje lub że zaraz nastąpi zaskoczenie</li>
              <li><strong>Relatable Pain lub Gain</strong> — dotknij bólu lub pragnienia konkretnego odbiorcy</li>
              <li><strong>Specificity</strong> — nadaj konkretność: liczba, czas, sytuacja, kontekst</li>
            </ol>

            <p>
              Przykład bez formuły: „Jak pisać lepsze hooki."<br />
              Przykład z formułą: „90% twórców traci odbiorców w pierwszych 2 sekundach przez jeden błąd, który popełniają w każdym poście."
            </p>

            <h3>5-punktowy scoring hooka przed publikacją</h3>

            <p>Oceń hook przed opublikowaniem — każde kryterium daje 0–2 punkty (max 10):</p>

            <ol>
              <li><strong>Jasność</strong> — odbiorca rozumie w 2 sekundy, o czym jest treść?</li>
              <li><strong>Obietnica wartości</strong> — konkretna korzyść lub rozwiązanie jest sygnalizowane?</li>
              <li><strong>Luka informacyjna</strong> — tworzy napięcie „chcę wiedzieć więcej"?</li>
              <li><strong>Emocja</strong> — wywołuje rozpoznawalną emocję?</li>
              <li><strong>Trafność</strong> — konkretny odbiorca tej treści zainteresuje się?</li>
            </ol>

            <p>
              Hook 9–10/10: publikuj. Hook 6–8/10: popraw jeden element. Hook poniżej 6: napisz od nowa.
            </p>

            <h2>Czego unikać</h2>

            <ul>
              <li><strong>Zbyt ogólne</strong> — „Mam coś ważnego do powiedzenia" nie mówi nic.</li>
              <li><strong>Clickbait bez pokrycia</strong> — obietnica, której treść nie spełnia. Algorytmy karzą niski completion rate.</li>
              <li><strong>Identyczny format w kółko</strong> — ta sama formuła przez 10 postów z rzędu tresuje odbiorców do ignorowania.</li>
              <li><strong>Zbyt długi</strong> — hook dłuższy niż 2 zdania w wideo lub 2–3 linie w poście to za dużo.</li>
              <li><strong>Fałszywa pilność</strong> — „ostatnia szansa" bez kontekstu jest wyczuwalna i psuje zaufanie.</li>
              <li><strong>Vague teaser</strong> — „tego nie możesz przegapić" bez wskazówki, czego dotyczy, nie tworzy luki, tylko irytację.</li>
            </ul>

            <h2>Podsumowanie</h2>

            <p>
              Przed każdym postem napisz 3 wersje hooka: jedną opartą na pytaniu, jedną na twierdzeniu kontrariańskim, jedną na bólu odbiorcy. Wybierz najsilniejszy: „Czy to zdanie zatrzymałoby mnie podczas scrollowania?"
            </p>

            <p>
              Analizuj wyniki co miesiąc: które hooki zatrzymują scrollowanie (3-sekundowe wyświetlenia wideo), które generują kliknięcie „więcej" w opisie, które powodują komentarze i udostępnienia. Dane są jedynym uczciwym nauczycielem.
            </p>

            <p>
              Buduj bank hooków: zapisuj każdy hook, który zatrzymał cię podczas scrollowania — cudzy lub własny. Rozkładaj go na czynniki: format, kąt, emocja. Z czasem wypracujesz wyczucie, którego nie da się w pełni skodyfikować regułami.
            </p>

            <p>
              Rotuj formaty — algorytmy i odbiorcy nagradzają różnorodność. Stosuj rotację między 5–7 różnymi stylami hooków, żeby nie trenować odbiorców na ignorowanie jednego wzorca.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            className="mt-10 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-8 text-center"
          >
            <h2 className="font-semibold text-xl mb-2">Chcesz generować hooki, które zatrzymują scrollowanie?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Viraluj.pl tworzy angażujące scenariusze z wbudowanymi hookami — na podstawie treści, które już działają w Twojej niszy.
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium text-sm px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
            >
              Wypróbuj Viraluj.pl
            </Link>
          </motion.div>
        </article>
      </section>

      <Footer />
    </div>
  );
};

export default BlogHooki;
